
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import store from '@/store';
import * as Sentry from '@sentry/vue';
import StripePaymentMethodService from '@/Services/Subscription/StripePaymentMethodService';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import VeeValidateRefs from '@/Models/VeeValidateRefs';
import Authenticator from '@/Services/Auth/Authenticator';
import { User } from '@/classes/auth';
import Icon from '@/components/Icons/Icon.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

@Component({
  components: {
    HelpTooltip: () => import('@/components/HelpTooltip.vue'),
    Icon,
    ValidationObserver,
    ValidationProvider,
  },
})
export default class PaymentMethodEdit extends Vue {
  @Prop(Boolean)
  open!: boolean;

  get isAbTest(): boolean {
    return store.state.auth.team.ab_tests?.includes('CNV_1609');
  }

  // region DATA
  // mapState
  user: User = Authenticator.getUser();

  step = 1;
  openFindVatModal = false;
  // Prevents double click issues and shows state of saving
  isSaving = false;
  // Handles payment method logic and some of the form state
  stripePaymentMethodService: StripePaymentMethodService = new StripePaymentMethodService();
  // Handles business logic and some of the form state
  stripeCheckoutService: StripeCheckoutService = new StripeCheckoutService();

  isPaymentFormError = false;

  enableDirectDebit: boolean = process.env.VUE_APP_FEATURE_SEPA === 'true'; // Switch to disable/enable direct debit

  $refs!: {
    // Used for validation of the form data<ValidationObserver slim ref="observer">
    observer: VeeValidateRefs;
  };
  // endregion DATA END

  // region METHODS
  cancel(): void {
    this.$emit('cancel');

    setTimeout(() => {
      this.step = 1;
    }, 500);
  }

  // Rebuild stripe elements form an exception is send_invoice which doesn't need stripe elements
  rebuildStripeForm(paymentMethodType: string): void {
    switch (paymentMethodType) {
      case 'card':
        this.stripePaymentMethodService
          .initCardForm()
          .catch(() => {
            store.dispatch('layout/setSnackbar', {
              type: 'error',
              icon: 'forbidden-system',
              text: this.$t('fields.account.billing.payment_edit.rebuild_error'),
              isSnackbar: true,
            });
            this.isPaymentFormError = true;
          })
          .finally(() => {
            // We do not have to manually trigger loader state since it will be triggered inside StripePaymentMethodService
            // this.stripePaymentMethodService.isPaymentFormLoaded = true;
          });
        break;
      case 'sepa_debit':
        this.stripePaymentMethodService
          .initSEPAForm()
          .catch(() => {
            store.dispatch('layout/setSnackbar', {
              type: 'error',
              icon: 'forbidden-system',
              text: this.$t('fields.account.billing.payment_edit.rebuild_error'),
              isSnackbar: true,
            });
            this.isPaymentFormError = true;
          })
          .finally(() => {
            // We do not have to manually trigger loader state since it will be triggered inside StripePaymentMethodService
            // this.stripePaymentMethodService.isPaymentFormLoaded = true;
          });
        break;
    }
  }

  // Save the payment method as new default
  async submitPaymentMethod(): Promise<any> {
    await this.stripeCheckoutService.updateCustomerVatRate();

    return this.stripePaymentMethodService.savePaymentMethod({
      legal_entity: store.state.auth.team.legal_entity,
      customer: this.stripeCheckoutService.customer,
    });
  }

  async onPaymentMethodSave(): Promise<void> {
    if ((await this.$refs.observer.validate()) && this.isSubmitPossible()) {
      this.isSaving = true;
      const result = await this.submitPaymentMethod();
      if (result?.status === 200) {
        let team = store.state.auth.team;
        team.is_payment_method_invoice = this.stripePaymentMethodService.paymentType === 'send_invoice';
        store.dispatch('auth/updateTeam', team);
        this.$emit('update-payment-method');
        this.$emit('update-billing-address');
        this.$emit('cancel');
        this.isSaving = false;
        this.step = 1;

        await store.dispatch('layout/setSnackbar', {
          type: 'info',
          icon: 'info-system',
          text: this.$t('fields.account.billing.payment_edit.save_ok'),
          isSnackbar: true,
        });
      } else {
        let errorMessage = '';
        if (result?.response?.status === 402) {
          errorMessage = this.$t(
            'fields.checkout.form.stripe_errors.' +
              (result.response.data.decline_code || result.response.data.error_code)
          ).toString();
        }
        this.isSaving = false;
        Sentry.captureException(result, {
          user: {
            id: Authenticator.getUser().id,
            email: Authenticator.getUser().email,
            team_id: Authenticator.getTeam().id,
          },
          tags: {
            method: 'onPaymentMethodSave',
          },
        });

        await this.$store.dispatch('layout/setSnackbar', {
          type: 'error',
          icon: 'forbidden-system',
          text: errorMessage || this.$t('fields.checkout.form.invalid'),
          isSnackbar: true,
        });
      }
    }
  }

  // We fill the form for the customer to ease the pain of filling out
  handlePrefillData(): void {
    // Init customer data for prefilling the form
    this.stripeCheckoutService.initCustomerData();
  }

  // No saving process going on and stripe form is ready?
  isSubmitPossible(): boolean {
    return !this.isSaving && !this.isPaymentFormError;
  }

  continueStep(): void {
    this.stripePaymentMethodService.paymentType === 'send_invoice' ? this.onPaymentMethodSave() : (this.step = 2);
  }
  // endregion METHODS END

  // region COMPUTED METHODS
  get currentTitle(): string {
    switch (this.step) {
      case 1:
        return this.$t('fields.account.billing.payment_edit.title_one').toString();
      case 2:
        return this.$t('fields.account.billing.payment_edit.title_two').toString();
    }
    return '';
  }
  // endregion COMPUTED METHODS EN

  // region WATCHERS
  @Watch('open')
  onOpenChange(open: boolean): void {
    if (!open) {
      return;
    }
    this.rebuildStripeForm(this.stripePaymentMethodService.paymentType);
  }

  /**
   * If type changes due to clicking on payment selection trigger a change
   * of the payment method form.
   *
   * @param newVal
   */
  @Watch('stripePaymentMethodService.paymentType', { deep: true, immediate: false })
  onPaymentMethodUpdate(newVal: string): void {
    if (!this.open) {
      return;
    }
    this.rebuildStripeForm(newVal);
  }
  // endregion WATCHERS END

  // region HOOKS
  // Mount the payment form, we do this here because refs is not available in created()!
  // Setup the components needed data, like customer data.
  async created(): Promise<void> {
    this.handlePrefillData();

    this.stripePaymentMethodService.getAllowedPaymentMethodsForUser(this.stripeCheckoutService.country.country_iso2);
  }
  // endregion HOOKS END
}
